import { useGetOneJob } from "#hooks/adapters/useJobs";
import { useStickyState } from "#src/hooks/useStickyState";
import { Banner, type BannerProps } from "@validereinc/common-components";
import { JobStatus, JobType } from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useEffect, useMemo } from "react";
import { useTimeoutHandler } from "../Banners.helpers";
import styles from "./BulkUpdateProgressBanner.module.scss";
import { useBulkActionToast } from "#src/components/hooks/useBulkActionToast";

const cx = classNames.bind(styles);

export const BulkUpdateProgressBanner = ({
  jobStorageKey,
  setSelectedItems,
  setIsRunning,
  setIsSelectionInverted,
  invalidate,
  subject,
  action,
}: {
  jobStorageKey: string;
  setSelectedItems?: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  setIsRunning?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSelectionInverted?: React.Dispatch<React.SetStateAction<boolean>>;
  invalidate: () => void;
  subject: {
    singular: string;
    plural: string;
  };
  action: string | ((job?: JobType) => string);
}) => {
  const [jobId, setJobId] = useStickyState<string>("", jobStorageKey);
  const { data: jobData, isLoading: isJobLoading } = useGetOneJob(jobId);
  const job: JobType = jobData?.data;

  const subjectToUse =
    job?.total_items === 1 ? subject.singular : subject.plural;
  const actionToUse = typeof action === "string" ? action : action(job);

  const reset = () => {
    invalidate();

    setIsRunning?.(false);
    setSelectedItems?.({});
    setIsSelectionInverted?.(false);

    setTimeout(() => {
      setJobId("");
    }, 10000);
  };

  useTimeoutHandler(
    job,
    `Failed to run bulk ${actionToUse} ${subjectToUse} job due to timeout`,
    reset
  );

  useBulkActionToast({
    job,
    resetJob: reset,
    actionLabel: `${actionToUse} ${subjectToUse}`,
  });

  const handleDismiss = () => {
    setJobId("");
  };

  const jobBannerProps: Pick<
    BannerProps,
    "variant" | "titleText" | "description"
  > = useMemo(() => {
    switch (job?.status) {
      case JobStatus.FINISHED:
        if (job?.item_status_counts.success < job?.total_items) {
          return {
            variant: "warning",
            titleText: [
              `Successfully ${actionToUse}d ${job?.item_status_counts.success}/${job?.total_items} ${subjectToUse}.`,
              job?.item_status_counts.failed > 0
                ? ` Failed to ${actionToUse} ${job?.item_status_counts.failed} ${subjectToUse}.`
                : "",
              job?.item_status_counts.skipped > 0
                ? ` Skipped ${job?.item_status_counts.skipped} ${subjectToUse}.`
                : "",
            ].join(" "),
            description: `Review the failed ${subjectToUse}, address all dependencies, and try to ${actionToUse} them again.`,
          };
        } else {
          return {
            variant: "success",
            titleText: `Successfully ${actionToUse}d ${job?.total_items} ${subjectToUse}.`,
          };
        }
      case JobStatus.FAILED:
        return {
          variant: "error",
          titleText: `Failed to ${actionToUse} ${job?.total_items} ${subjectToUse}.`,
        };
      case JobStatus.STARTED:
      default:
        return {
          variant: "loading",
          titleText: `Updating ${job?.total_items} ${subjectToUse}...`,
        };
    }
  }, [job?.status]);

  useEffect(() => {
    if ([JobStatus.FAILED, JobStatus.FINISHED].includes(job?.status)) {
      reset();
    }
  }, [job?.status]);

  const isLoading = Boolean(jobId) && isJobLoading;

  useEffect(() => {
    if (isLoading) {
      setIsRunning?.(true);
    }
  }, [isLoading]);

  if (isLoading) {
    return (
      <Banner
        className={cx("banner")}
        variant={isLoading ? "loading" : "warning"}
        titleText={
          isLoading
            ? `Fetching bulk ${actionToUse} ${subjectToUse} job details...`
            : `Couldn't fetch bulk ${actionToUse} ${subjectToUse} job details.`
        }
        isDismissable={!isLoading}
        onDismiss={handleDismiss}
      />
    );
  }

  return job ? (
    <Banner
      className={cx("banner")}
      {...jobBannerProps}
      isDismissable
      onDismiss={handleDismiss}
    />
  ) : null;
};
